import React from "react";
import { graphql } from "react-relay";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faArrowFromLeft, faEllipsisV, faPen, faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import * as fzy from "fzy.js";

import SettingsPageHeader from "~/account/SettingsPageHeader";
import { HeaderAction } from "~/common/PageHeader";
import { Query } from "~/lib/relayUtil";
import store from "~/lib/store";
import DocumentTitle from "~/lib/title";
import HomePanelGroupsMenu from "~/stream/HomePanelGroupsMenu";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import TagComponent from "./TagComponent";
import { launchCreateTagModal, launchEditTagModal } from "./TagModal";

import type { HomePanelGroupsMenu_viewer$key } from "~/__generated__/HomePanelGroupsMenu_viewer.graphql";
import type {
	WorkspaceTagsPageTagsQuery$data,
	WorkspaceTagsPageTagsQuery as WorkspaceTagsPageTagsQueryType,
} from "~/__generated__/WorkspaceTagsPageTagsQuery.graphql";

import styles from "./WorkspaceTagsPage.module.css";

const INFO_BLOCK_DISMISSED_KEY = "workspaceTags_InfoBlockDismissed";

const InfoBlock = React.memo(function InfoBlock() {
	const [dismissed, setDismissed] = store.useState(INFO_BLOCK_DISMISSED_KEY, false);
	const dismiss = React.useCallback(() => setDismissed(true), [setDismissed]);

	return dismissed ? null : (
		<section className={styles.infoBlock}>
			<IconButton
				icon={faTimes}
				onClick={dismiss}
				style={{ float: "right", margin: "-0.25em" /* align against icon, not button */ }}
			/>
			<h1>What are tags?</h1>
			<p>
				Tags are a great way to categorize content across your workspace and organize your search results. Tags are
				editable, and any changes made to a tag are visible to all members of your workspace. We’ve created a few
				default tags to get you started – here are some ways you can use them:
			</p>
			<ul>
				<li>
					Identify when things are holding you up <TagComponent label="blocker" color="NO_COLOR" />
				</li>
				<li>
					Tag external <TagComponent label="customer" color="NO_COLOR" /> information
				</li>
				<li>
					Track when your team has come to a <TagComponent label="decision" color="NO_COLOR" />
				</li>
				<li>
					Easily find and organize a key <TagComponent label="takeaway" color="NO_COLOR" />
				</li>
				<li>
					Prioritize the most important work <TagComponent label="urgent" color="NO_COLOR" />
				</li>
			</ul>
		</section>
	);
});

const WorkspaceTagsPageTagsQuery = graphql`
	query WorkspaceTagsPageTagsQuery {
		user {
			id
			internalAttributes {
				isAccountAdmin
			}
		}
		account {
			id
			tags {
				...HomePanelGroupsMenu_object
				id
				label
				color
				createdBy {
					id
					fullName
				}
				lastChangedBy {
					id
					fullName
				}
				homePanelGroup {
					id
				}
			}
		}
		viewer {
			...HomePanelGroupsMenu_viewer
		}
	}
`;

interface TagListItemProps {
	userId: string;
	tag: WorkspaceTagsPageTagsQuery$data["account"]["tags"][number];
	viewer: HomePanelGroupsMenu_viewer$key;
}

const TagListItem: React.FC<TagListItemProps> = ({ userId, tag, viewer }) => {
	const { label, color, createdBy, lastChangedBy } = tag;

	const launchTagModal = React.useCallback(
		() => launchEditTagModal({ tagId: tag.id, startingLabel: tag.label, startingColor: tag.color }),
		[tag],
	);

	const determineAttributionText = () => {
		if (!lastChangedBy) {
			if (userId === createdBy?.id) return "Created by me";
			else return `Created by ${createdBy?.fullName ?? "default"}`;
		}

		if (userId === lastChangedBy.id) return "Last changed by me";
		else return `Last changed by ${lastChangedBy.fullName}`;
	};

	return (
		<div className={styles.tagListItem}>
			<TagComponent className={styles.tagListTag} label={label} color={color} />
			<IconButton className={styles.editButton} onClick={launchTagModal} icon={faPen} />
			<div className={styles.attribution}>{determineAttributionText()}</div>
			<Menu>
				<Menu.Trigger>
					<IconButton className={styles.homePanelMenuTrigger} icon={faEllipsisV} />
				</Menu.Trigger>
				<Menu.Portal>
					<Menu.Content align="end">
						<Menu.Sub>
							<Menu.SubTrigger icon={faArrowFromLeft}>
								{tag.homePanelGroup?.id ? "Move to group" : "Add to home panel"}
							</Menu.SubTrigger>
							<Menu.Portal>
								<Menu.SubContent>
									<HomePanelGroupsMenu object={tag} viewer={viewer} />
								</Menu.SubContent>
							</Menu.Portal>
						</Menu.Sub>
					</Menu.Content>
				</Menu.Portal>
			</Menu>
		</div>
	);
};

const Tags: React.FC<WorkspaceTagsPageTagsQuery$data> = ({ account, user, viewer }) => {
	const { tags } = account;
	const homePanelTags = tags.filter(({ homePanelGroup }) => homePanelGroup != null);

	type TabType = "search" | "all" | "me" | "homepanel";
	const [activeTab, setActiveTab] = React.useState<TabType>("all");
	const [searchString, setSearchString] = React.useState("");

	React.useEffect(() => {
		if (searchString) setActiveTab("search");
	}, [searchString]);

	React.useEffect(() => {
		if (homePanelTags.length === 0 && activeTab === "homepanel") setActiveTab("all");
	}, [homePanelTags, activeTab]);

	const openCreateTagModal = React.useCallback(() => launchCreateTagModal(), []);

	const onSearch = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchString(event.target.value);
	}, []);

	const onTabClick = React.useCallback(
		(key: TabType) => () => {
			setActiveTab(key);
		},
		[],
	);

	const emptyMessages: { [k in TabType]: string } = {
		all: 'Use the "Create tag" button to get started.',
		search: "No results.",
		me: 'Use the "Create tag" button to get started.',
		homepanel: "You have not added any tags to your home panel.",
	};

	const pageSubject = (
		<>
			<div>Workspace Tags</div>
			<div className={styles.subSubject}>Use tags to help organize content in your workspace</div>
		</>
	);

	const headerActions = [
		<HeaderAction key="create-tag" onClick={openCreateTagModal} variant="primary" slim>
			Create tag
		</HeaderAction>,
	];

	const sortedTags = React.useMemo(() => {
		let sortedTags: WorkspaceTagsPageTagsQuery$data["account"]["tags"] = [...tags].sort((tagA, tagB) =>
			tagA.label.localeCompare(tagB.label),
		);

		if (activeTab === "search" && searchString !== "") {
			sortedTags = sortedTags
				.filter(tag => fzy.hasMatch(searchString, tag.label))
				.map(tag => ({
					...tag,
					score: fzy.score(searchString, tag.label),
				}))
				.sort((tagA, tagB) => tagB.score - tagA.score);
		} else if (activeTab === "me") {
			sortedTags = sortedTags.filter(tag => user.id === tag.createdBy?.id || user.id === tag.lastChangedBy?.id);
		} else if (activeTab === "homepanel") {
			sortedTags = sortedTags.filter(({ homePanelGroup }) => homePanelGroup != null);
		}
		return sortedTags;
	}, [tags, activeTab, searchString, user.id]);

	return (
		<div className={styles.pageContainer}>
			<DocumentTitle title="Workspace Tags" />
			<SettingsPageHeader
				subject={pageSubject}
				actions={headerActions}
				backButton={<></>} // Has to be truthy, or else it'll be overridden
			/>
			<div className={styles.navBar}>
				<nav>
					<button
						className={cx(styles.tabButton, { [styles.active]: activeTab === "all" })}
						onClick={onTabClick("all")}
					>
						All tags
					</button>
					<button className={cx(styles.tabButton, { [styles.active]: activeTab === "me" })} onClick={onTabClick("me")}>
						My tags
					</button>
					{homePanelTags.length != 0 && (
						<button
							className={cx(styles.tabButton, { [styles.active]: activeTab === "homepanel" })}
							onClick={onTabClick("homepanel")}
						>
							In my home panel
						</button>
					)}
				</nav>
				<label className={styles.searchWrapper}>
					<FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
					<input
						type="text"
						placeholder="Search tags"
						value={searchString}
						onChange={onSearch}
						onFocus={onTabClick("search")}
					/>
				</label>
			</div>
			<InfoBlock />
			{sortedTags.length === 0 ? (
				emptyMessages[activeTab]
			) : (
				<div className={styles.tagList}>
					{sortedTags.map(tag => (
						<TagListItem key={tag.id} tag={tag} userId={user.id} viewer={viewer} />
					))}
				</div>
			)}
		</div>
	);
};

export default function TagsQueryRenderer() {
	return (
		<Query<WorkspaceTagsPageTagsQueryType> query={WorkspaceTagsPageTagsQuery}>{props => <Tags {...props} />}</Query>
	);
}
